import { DateTime } from "luxon";

import { PassengerTransferType } from "@/constants/passengerTransferTypes";
import { CompanionsSchema } from "@/schemas/companions";
import { PassengerHistoryTypes } from "@/schemas/passenger-history.schemas";

import { TASK_STATUS } from "../constants";
import { PASSENGER_GROUP_STATUS, PASSENGER_STATUS } from "../constants/status";
import { IAddressData } from "./address.types";
import { PassengerExtras, PassengerTypes } from "./global";

export type IPassengersGroup = {
    id: string;
    name: string;
    languages: string[];

    companions: string[];

    notes: string;
    headPhotoUrl: string | null;

    origin: IAddressData;
    destination: IAddressData;

    pickUp: DateTime | null;
    dropOff: DateTime | null;

    status?: PASSENGER_STATUS | null;
    flightNumber: string;

    isFlightArrival: boolean;
    isSelfTransport: boolean;

    flightTime: DateTime | null;

    cancelled: boolean;

    UETDS: boolean;

    // Customs
    originalOrigin: string | null;
    originalDestination: string | null;
    originalPickUp: DateTime | null;
    originalDropOff: DateTime | null;
    vip: string;
    passengerType: PassengerTypes;
};

export type IPassengerDataStatusMilestonesItem = {
    action_time: string;
    action_type: PassengerHistoryTypes;
} | null;
export type IPassengerDataStatusMilestones = Record<
    keyof typeof PASSENGER_GROUP_STATUS,
    IPassengerDataStatusMilestonesItem
>;

export const PASSENGER_DEFAULT_STATUS_MILESTONES: IPassengerDataStatusMilestones = Object.keys(
    PASSENGER_GROUP_STATUS
).reduce((prev, curr) => {
    prev[curr as keyof typeof PASSENGER_GROUP_STATUS] = null;
    return prev;
}, {} as IPassengerDataStatusMilestones);

export type IResPassengersSearchResult = {
    arrival?: IPlanPassenger<"db">;
    departure?: IPlanPassenger<"db">;
    passengerType: PassengerTypes;
    id: string;
    name: string;
};

export type IPlanPassenger<T extends "api" | "db"> = {
    id: string;
    name: string;
    languages: string[];

    people: number;
    companions: string[];
    fullCompanions: CompanionsSchema;

    notes: string;
    headPhotoUrl: string | null;

    origin: IAddressData;
    destination: IAddressData;

    pickUp: T extends "api" ? DateTime | null : string;
    dropOff: T extends "api" ? DateTime | null : string;

    status?: PASSENGER_STATUS | null;
    flightNumber: string;

    isFlightArrival: boolean;
    isSelfTransport: boolean;

    flightTime: T extends "api" ? DateTime | null : string;

    cancelled: boolean;

    // Customs
    originalOrigin: string | null;
    originalDestination: string | null;
    originalPickUp: DateTime | null;
    originalDropOff: DateTime | null;
    vip: string;
    airport: string;
    location: string;

    driverId: string | null;
    uetdsId: string | null;
    uetdsPdfUrl: string | null;
    passengerType: PassengerTypes;
    taskId: string | null;
    extras: PassengerExtras | null;
    transferType: PassengerTransferType;

    hotelName: string;
    statusMilestones: IPassengerDataStatusMilestones;
};

export type PassengersGroupTableRow = IPassengersGroup & {
    id: string;

    notes: string;

    taskId: string | null;
    driver: string | null;
    driverId: string | null;
    location: string;
    airport: string;

    duration: string;

    // Customs
    originalOrigin: string | null;
    originalDestination: string | null;
    originalPickUp: DateTime | null;
    originalDropOff: DateTime | null;

    UETDS: boolean;
    UETDSPdfUrl: string | null;
    transferType: PassengerTransferType;
};
export type RES_PassengerGroup = Omit<IPassengersGroup, "pickUp" | "dropOff" | "flightTime"> & {
    pickUp: string | null;
    dropOff: string | null;
    flightTime: string | null;
    pickupEdited: boolean;
    people: number;
};

export type Task<T extends IPlanPassenger<"api"> | IPassengersGroup = IPassengersGroup> = {
    id: string; // From DB
    passengers: T[];
    driverId: string;
    driverName: string;
    origin: IAddressData;
    destination: IAddressData;
    status: keyof typeof TASK_STATUS;
    isArrival: boolean;
    pickUp: DateTime;
    dropOff: DateTime;

    // Customs
    originalOrigin: IAddressData;
    originalDestination: IAddressData;
    originalPickUp: DateTime;
    originalDropOff: DateTime;

    uetdsId: string | null;
    uetdsPdfUrl: string | null;
};
export type RES_Task = Omit<Task, "pickUp" | "dropOff" | "customPickUp" | "customDropOff" | "passengers"> & {
    passengers: RES_PassengerGroup[];
    pickUp: string;
    dropOff: string;
    originalPickUp: string;
    originalDropOff: string;
};

export type IIndexedTasks = { [key: string]: Task };

export type TaskEditionPayload = {
    status: keyof typeof TASK_STATUS;
    customPickUp: DateTime | null;
    customDropOff: DateTime | null;
    customOrigin: string | null;
    customDestination: string | null;
};

export type RES_SaveDataGridChanges = {
    changes: {
        [id: string]: {
            assignation?: {
                allowed: boolean;
                assigned: boolean;
                error?: string;
                task: Task;
            };
            assignationRes?: true | string;
            data: boolean;
        };
    };
};
